import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchApi,
  FormManager,
  makeResetCache,
  notifyError,
  notifyResponse,
  PageTitle,
  Create,
  useCacheRecord,
} from 'gossamer-front-end';
import {
  AdminInstitutionGroupEventBulkCopy,
  Event,
  EventExtendedView,
  InstitutionGroup,
  InstitutionGroupEvent,
  InstitutionGroupEventView,
  ReportInstitutionGroupView,
} from 'cb-model';
import { Entity, FieldType, getFilter, getListRowLink, PersistenceType, ReferenceField } from 'gossamer-unified';

type BulkCopyFormRow = AdminInstitutionGroupEventBulkCopy;

const InstitutionGroupEventBulkCopyForm: Entity<BulkCopyFormRow> = {
  id: 'InstitutionGroupEventBulkCopyForm',
  title: 'Institution Group Event Bulk Copy',
  primaryKeyFields: [],
  fields: {
    toGroupId: {
      seq: 10,
      type: FieldType.Reference,
      label: 'Copy events into this group',
      refEntity: InstitutionGroup,
      mandatory: true,
      persistence: PersistenceType.FrontEndOnly,
      description: 'Target group that will receive the copied events',
    } as ReferenceField,
    fromGroupId: {
      seq: 20,
      type: FieldType.Reference,
      label: 'Copy events from this group',
      refEntity: InstitutionGroup,
      mandatory: true,
      persistence: PersistenceType.FrontEndOnly,
      description: 'Source group whose linked events will be copied',
    } as ReferenceField,
    shiftNumberOfWeeks: {
      seq: 30,
      type: FieldType.Integer,
      label: 'Shift number of weeks',
      mandatory: true,
      persistence: PersistenceType.FrontEndOnly,
      description: 'How many weeks to move each copied event by',
    },
  },
};

export const InstitutionGroupEventBulkCopy = (): React.JSX.Element => {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const resetCache = makeResetCache(dispatch);
  const record = useCacheRecord(InstitutionGroup, params.key);

  const handleExit = () => navigate(getListRowLink(InstitutionGroup.listRowLink, params.key));

  const institutionId = record.data?.institution_id;
  const groupId = params.key ? parseInt(params.key, 10) : NaN;

  const localEntity = React.useMemo(() => {
    const next = {
      ...InstitutionGroupEventBulkCopyForm,
      fields: {
        ...InstitutionGroupEventBulkCopyForm.fields,
        toGroupId: {
          ...InstitutionGroupEventBulkCopyForm.fields.toGroupId,
          queryOptions: institutionId
            ? {
                where: {
                  and: [getFilter('title', 'ct', ''), getFilter('institution_id', 'eq', institutionId, 'hidden')],
                },
                order: [['title', 'ASC']],
              }
            : undefined,
        } as ReferenceField,
        fromGroupId: {
          ...InstitutionGroupEventBulkCopyForm.fields.fromGroupId,
          queryOptions: institutionId
            ? {
                where: {
                  and: [getFilter('title', 'ct', ''), getFilter('institution_id', 'eq', institutionId, 'hidden')],
                },
                order: [['title', 'ASC']],
              }
            : undefined,
        } as ReferenceField,
      },
    };
    return next;
  }, [institutionId]);

  return (
    <>
      <PageTitle
        title="Bulk Copy Group Events"
        subtitle="Create copied events from one group into another and move them by a whole number of weeks (excluding On-Demand events)"
      />
      {!record.data && <div>Loading group...</div>}
      {!!record.data && (
        <FormManager
          handleCancel={handleExit}
          handleSuccess={handleExit}
          handleSubmit={async (values) => {
            const main = values.main as unknown as {
              toGroupId?: number | string | null;
              fromGroupId?: number | string | null;
              shiftNumberOfWeeks?: number | string | null;
            };

            const payload: AdminInstitutionGroupEventBulkCopy = {
              toGroupId: Number(main.toGroupId),
              fromGroupId: Number(main.fromGroupId),
              shiftNumberOfWeeks: Number(main.shiftNumberOfWeeks),
            };

            try {
              const response = await fetchApi('POST', '/main/institution-group-event-bulk-copy', payload);
              resetCache(Event.id);
              resetCache(EventExtendedView.id);
              resetCache(InstitutionGroupEvent.id);
              resetCache(InstitutionGroupEventView.id);
              resetCache(ReportInstitutionGroupView.id);
              notifyResponse(response);
              handleExit();
            } catch (e) {
              notifyError(e, 'An error occurred while bulk copying group events');
            }
          }}
          initialization={{
            main: {
              type: 'create-single',
              entity: localEntity,
              override: {
                toGroupId: Number.isFinite(groupId) ? groupId : undefined,
                shiftNumberOfWeeks: 1,
              },
            },
          }}
        >
          <Create valuesKey="main" entity={localEntity} subtitle={`Target group: ${record.data.title}`} />
        </FormManager>
      )}
    </>
  );
};
